import {
  Card,
  CardHeader,
  Combobox,
  DateRangePicker,
  useIsWideViewport,
  type DateRange,
} from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import { useState } from 'react';
import { portalApi } from '../lib/portal-api.js';
import { civilDateOf, formatHourMinute } from '../lib/time-zone.js';
import { EmptyCard, ErrorCard, FilterBar, LoadingCard, Pagination } from './data-view.js';
import { CustomerAppointmentDetail } from './customer-appointment-detail.js';
import { CustomerAppointmentStatus } from './customer-appointment-status.js';
import { customerAppointmentStatuses as statusLabels } from './customer-appointment-labels.js';
/// Os pedidos da pessoa em todas as empresas do portal (ADR 0067), com filtro por empresa.
export function CustomerAppointments({
  companies,
}: {
  companies: Array<{ key: string; displayName: string }>;
}) {
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(25);
  const [companyKey, setCompanyKey] = useState('ALL');
  const [search, setSearch] = useState('');
  const [range, setRange] = useState<DateRange>({ from: null, to: null });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const wide = useIsWideViewport();
  const [status, setStatus] = useState('ALL');
  const [direction, setDirection] = useState('desc');
  const result = useQuery({
    queryKey: [
      'customer',
      'portal',
      'appointments',
      companyKey,
      offset,
      limit,
      search,
      range,
      status,
      direction,
    ],
    queryFn: () =>
      portalApi.appointments({
        limit,
        offset,
        companyKey: companyKey === 'ALL' ? undefined : companyKey,
        search,
        from: range.from ?? undefined,
        to: range.to ?? undefined,
        status,
        direction,
      }),
    retry: false,
    refetchInterval: 15000,
  });
  const appointments = result.data?.appointments ?? [];
  const selected = appointments.find((item) => item.id === selectedId) ?? appointments[0];
  return (
    <section className="customer-appointments mb-8">
      <Card className="border-0 bg-transparent p-0 shadow-none">
        <CardHeader title="Meus agendamentos" icon={<CalendarDays className="size-4" />} />
        <FilterBar
          showViewPicker={false}
          searchId="customer-appointment-search"
          searchPlaceholder="Buscar serviço ou empresa"
          chips={[
            ...(search
              ? [
                  {
                    key: 'search',
                    label: `Busca: ${search}`,
                    onRemove: () => {
                      setSearch('');
                      setOffset(0);
                    },
                  },
                ]
              : []),
            ...(companyKey !== 'ALL'
              ? [
                  {
                    key: 'company',
                    label:
                      companies.find((company) => company.key === companyKey)?.displayName ??
                      'Empresa selecionada',
                    onRemove: () => {
                      setCompanyKey('ALL');
                      setOffset(0);
                    },
                  },
                ]
              : []),
            ...(status !== 'ALL'
              ? [
                  {
                    key: 'status',
                    label: statusLabels[status as keyof typeof statusLabels],
                    onRemove: () => {
                      setStatus('ALL');
                      setOffset(0);
                    },
                  },
                ]
              : []),
            ...(range.from || range.to
              ? [
                  {
                    key: 'range',
                    label: 'Período escolhido',
                    onRemove: () => {
                      setRange({ from: null, to: null });
                      setOffset(0);
                    },
                  },
                ]
              : []),
          ]}
          onClear={() => {
            setSearch('');
            setCompanyKey('ALL');
            setStatus('ALL');
            setRange({ from: null, to: null });
            setOffset(0);
          }}
          resultsLabel={`Ver ${result.data?.total ?? 0} ${(result.data?.total ?? 0) === 1 ? 'agendamento' : 'agendamentos'}`}
          pageSize={limit}
          onPageSizeChange={(value) => {
            setLimit(value);
            setOffset(0);
          }}
          searchLabel="Buscar nos meus agendamentos"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setOffset(0);
          }}
          count={`${result.data?.total ?? 0} ${(result.data?.total ?? 0) === 1 ? 'agendamento' : 'agendamentos'}`}
        >
          {companies.length > 1 ? (
            <Combobox
              label="Empresa"
              placeholder="Todas as empresas"
              selectedLabel={
                companies.find((company) => company.key === companyKey)?.displayName ??
                'Todas as empresas'
              }
              options={[
                { value: 'ALL', label: 'Todas as empresas' },
                ...companies.map((company) => ({ value: company.key, label: company.displayName })),
              ]}
              onSelect={(choice) => {
                if (choice.kind === 'option') {
                  setCompanyKey(choice.value);
                  setOffset(0);
                }
              }}
            />
          ) : null}
          <DateRangePicker
            label="Período dos agendamentos"
            value={range}
            onValueChange={(value) => {
              setRange(value);
              setOffset(0);
            }}
            today={civilDateOf(new Date(), 'UTC')}
          />
          <Combobox
            label="Situação"
            placeholder="Todas"
            selectedLabel={
              status === 'ALL' ? 'Todas' : statusLabels[status as keyof typeof statusLabels]
            }
            options={[
              { value: 'ALL', label: 'Todas' },
              ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
            ]}
            onSelect={(choice) => {
              if (choice.kind === 'option') {
                setStatus(choice.value);
                setOffset(0);
              }
            }}
          />
          <Combobox
            label="Ordenar por data"
            placeholder="Mais recentes primeiro"
            selectedLabel={
              direction === 'desc' ? 'Mais recentes primeiro' : 'Mais antigos primeiro'
            }
            options={[
              { value: 'desc', label: 'Mais recentes primeiro' },
              { value: 'asc', label: 'Mais antigos primeiro' },
            ]}
            onSelect={(choice) => {
              if (choice.kind === 'option') {
                setDirection(choice.value);
                setOffset(0);
              }
            }}
          />
        </FilterBar>
        {result.isPending ? (
          <LoadingCard label="Carregando seus agendamentos" />
        ) : result.isError ? (
          <ErrorCard message="Não foi possível consultar seus agendamentos." />
        ) : result.data.appointments.length === 0 ? (
          <EmptyCard
            icon={CalendarDays}
            title="Nenhum agendamento encontrado"
            description="Encontre uma empresa na vitrine e peça um horário, ou ajuste os filtros."
          />
        ) : (
          <div className="mt-6 grid items-start gap-6 md:grid-cols-[minmax(0,.9fr)_minmax(0,1.4fr)]">
            <ol aria-label="Linha do tempo dos agendamentos" className="min-w-0 space-y-3">
              {appointments.map((appointment) => (
                <li
                  key={appointment.id}
                  className="relative grid grid-cols-[3.25rem_minmax(0,1fr)] gap-4 before:absolute before:bottom-[-.75rem] before:left-[3.65rem] before:top-0 before:w-px before:bg-surface-border last:before:bottom-1/2 sm:grid-cols-[4rem_minmax(0,1fr)] sm:before:left-[4.4rem]"
                >
                  <time
                    dateTime={appointment.startsAt}
                    id={`appointment-date-${appointment.id}`}
                    className="pt-4 text-right text-xs font-semibold text-content-secondary"
                  >
                    <span className="block">
                      {civilDateOf(appointment.startsAt, appointment.timeZone).slice(8)}/
                      {civilDateOf(appointment.startsAt, appointment.timeZone).slice(5, 7)}
                    </span>
                    <span className="mt-1 block text-[.65rem] font-normal text-content-muted">
                      {civilDateOf(appointment.startsAt, appointment.timeZone).slice(0, 4)}
                    </span>
                  </time>
                  <button
                    type="button"
                    aria-pressed={wide && selected?.id === appointment.id}
                    aria-describedby={`appointment-date-${appointment.id}`}
                    onClick={() => {
                      setSelectedId(appointment.id);
                      setMobileDetail(true);
                    }}
                    className={`relative min-w-0 cursor-pointer rounded-xl border bg-surface-card p-4 text-left transition before:absolute before:-left-[.65rem] before:top-6 before:size-2 before:rounded-full before:bg-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${wide && selected?.id === appointment.id ? 'border-brand-500 shadow-[inset_3px_0_0_rgb(var(--brand-500))]' : 'border-surface-border hover:border-brand-500'}`}
                  >
                    <span className="block text-xs font-semibold">
                      {formatHourMinute(appointment.startsAt, appointment.timeZone)}–
                      {formatHourMinute(appointment.endsAt, appointment.timeZone)}
                    </span>
                    <strong className="mt-1 block text-sm">{appointment.serviceDisplayName}</strong>
                    <span className="mt-1 block text-xs text-content-muted">
                      {appointment.companyName}
                    </span>
                    <span className="mt-3 block">
                      <CustomerAppointmentStatus appointment={appointment} />
                    </span>
                  </button>
                </li>
              ))}
            </ol>
            {wide && selected ? (
              <section aria-label="Detalhes do agendamento" className="min-w-0 md:sticky md:top-6">
                <CustomerAppointmentDetail
                  key={selected.id}
                  embedded
                  appointment={selected}
                  companyKey={selected.companyKey}
                  onClose={() => setMobileDetail(false)}
                />
              </section>
            ) : null}
          </div>
        )}
        {result.data ? (
          <Pagination
            total={result.data.total}
            limit={limit}
            offset={offset}
            onOffsetChange={setOffset}
            itemLabel={(count) => (count === 1 ? 'agendamento' : 'agendamentos')}
          />
        ) : null}
      </Card>
      {!wide && mobileDetail && selected ? (
        <CustomerAppointmentDetail
          key={selected.id}
          appointment={
            result.data?.appointments.find((item) => item.id === selected.id) ?? selected
          }
          companyKey={selected.companyKey}
          onClose={() => setMobileDetail(false)}
        />
      ) : null}
    </section>
  );
}
